// How many messages a session's record holds, for the session rows of the
// trace log. A row asks only while it is on screen, a row scrolled away before
// its turn is dropped from the queue, and every answer is kept for the page's
// lifetime so a row that comes back needs no second read. A count that could
// not be read is kept as null: the row says nothing rather than guessing.
//
// Two ways to answer. The conversation record summaries route counts a whole
// screen of sessions in one keyed statement, so the queue is drained in
// batches through it. A gateway without the route is remembered, and the
// queue falls back to one trace-bound count per session, a few at a time,
// each about a second on staging.
import { useEffect, useState } from "react";

const CONCURRENCY = 2;
const BATCH = 200;

// The data client is loaded on first use, not at import: the row renderers
// that use this module are also rendered without a window (server-side tests,
// static markup), and the client's module graph reads the window on load.
const defaultReader = async (id, sinceMs) => {
    const { query } = await import("./dataClient.js");
    return query("getSessionMessageCount", { id, sinceMs });
};
const defaultBatchReader = async (ids, sinceMs) => {
    const { query } = await import("./dataClient.js");
    return query("getSessionMessageCounts", { ids, sinceMs });
};

const counts = new Map();
const inflight = new Map();
const queue = [];
let running = 0;
let reader = defaultReader;
let batchReader = defaultBatchReader;
// null until the first batch answers; false once a gateway said it has no route.
let batchRoute = null;
let batchScheduled = false;

// A count as the reader answered it: a number, or null for "not known".
const known = (n) => (n == null || !Number.isFinite(Number(n)) ? null : Number(n));

function settle(job, n) {
    counts.set(job.id, n);
    inflight.delete(job.id);
    job.resolve(n);
}

function pump() {
    if (batchRoute !== false) {
        // The rows of one screen ask within one commit; a microtask later they
        // are one batch instead of one call for the first row and one for
        // the rest.
        if (!batchScheduled) {
            batchScheduled = true;
            queueMicrotask(() => {
                batchScheduled = false;
                pumpBatch();
            });
        }
        return;
    }
    while (running < CONCURRENCY && queue.length) {
        const job = queue.shift();
        if (job.cancelled) continue;
        running += 1;
        job.started = true;
        let read;
        try { read = Promise.resolve(reader(job.id, job.sinceMs)); } catch (error) { read = Promise.reject(error); }
        read
            .then(known, () => null)
            .then((n) => settle(job, n))
            .finally(() => {
                running -= 1;
                pump();
            });
    }
}

// One batch in flight at a time: everything queued goes in it (up to BATCH),
// the earliest window start among them widens the read for all of them.
function pumpBatch() {
    if (running > 0 || !queue.length) return;
    const jobs = [];
    while (queue.length && jobs.length < BATCH) {
        const job = queue.shift();
        if (job.cancelled) continue;
        job.started = true;
        jobs.push(job);
    }
    if (!jobs.length) return;
    running += 1;
    const sinceMs = jobs.reduce((min, job) => (Number.isFinite(job.sinceMs) && (min == null || job.sinceMs < min) ? job.sinceMs : min), null);
    let read;
    try { read = Promise.resolve(batchReader(jobs.map((job) => job.id), sinceMs)); } catch (error) { read = Promise.reject(error); }
    read
        .then((answer) => {
            batchRoute = true;
            for (const job of jobs) settle(job, known(answer instanceof Map ? answer.get(job.id) : undefined));
        }, (error) => {
            if (error?.recordRouteMissing) {
                // The gateway cannot batch: hand these back to the queue for
                // the one-per-session path, in their order.
                batchRoute = false;
                for (const job of jobs) job.started = false;
                queue.unshift(...jobs);
                return;
            }
            for (const job of jobs) settle(job, null);
        })
        .finally(() => {
            running -= 1;
            pump();
        });
}

// The count for `id`, from the cache or a queued read. Returns the settled
// value and a `cancel` that withdraws a read not yet started.
export function requestSessionMessageCount(id, sinceMs = null) {
    if (!id) return { promise: Promise.resolve(null), cancel: () => {} };
    if (counts.has(id)) return { promise: Promise.resolve(counts.get(id)), cancel: () => {} };
    let entry = inflight.get(id);
    if (!entry) {
        let resolve;
        const promise = new Promise((r) => { resolve = r; });
        const job = { id, sinceMs, resolve, started: false, cancelled: false, waiting: 0 };
        entry = { promise, job };
        inflight.set(id, entry);
        queue.push(job);
        pump();
    }
    entry.job.waiting += 1;
    const cancel = () => {
        entry.job.waiting -= 1;
        if (entry.job.waiting > 0 || entry.job.started) return;
        entry.job.cancelled = true;
        inflight.delete(id);
    };
    return { promise: entry.promise, cancel };
}

export function sessionMessageCount(id) {
    return counts.has(id) ? counts.get(id) : undefined;
}

// { count, loading } for a row: `count` is the number, null once a read
// failed, and undefined while unknown.
export function useSessionMessageCount(id, sinceMs = null) {
    const [state, setState] = useState(() => (id && counts.has(id)
        ? { count: counts.get(id), loading: false }
        : { count: undefined, loading: Boolean(id) }));
    useEffect(() => {
        if (!id) return undefined;
        if (counts.has(id)) {
            setState({ count: counts.get(id), loading: false });
            return undefined;
        }
        let live = true;
        setState({ count: undefined, loading: true });
        const { promise, cancel } = requestSessionMessageCount(id, sinceMs);
        promise.then((n) => { if (live) setState({ count: n, loading: false }); });
        return () => {
            live = false;
            cancel();
        };
    }, [id, sinceMs]);
    return state;
}

export function _setSessionMessageCountReaderForTests(fn, batch = null) {
    reader = fn || defaultReader;
    batchReader = batch || defaultBatchReader;
}
export function _seedSessionMessageCountsForTests(seed = {}) {
    for (const [id, n] of Object.entries(seed)) counts.set(id, n);
}
export function _resetSessionMessageCountsForTests() {
    counts.clear();
    inflight.clear();
    queue.length = 0;
    running = 0;
    batchRoute = null;
    batchScheduled = false;
}
export function _setSessionMessageCountBatchRouteForTests(value) {
    batchRoute = value;
}
